import { useMemo } from "react"
import { useCompanyConnectorControllerGetCompanyConnectorStatusQuery } from "~/ui-rtk/api/companyConnectorApi"
import { isSyncOutdated } from "./utils"
import { ConnectionStatus } from "../../utils"

// Setup states that indicate pending setup
const PENDING_SETUP_STATES = [
  "pending",
  "pending_setup",
  "incomplete",
  "needs_setup",
]

// DB statuses that signal the connection is broken or intentionally paused.
// The backend status endpoint surfaces these via setupState (APP-341) so the
// UI no longer has to infer them from sync recency.
const UNHEALTHY_STATES = [
  "broken",
  "paused",
  "delayed",
  "connecting",
  "updating",
]

export const useConnect = (id: string) => {
  const { data, isLoading } =
    useCompanyConnectorControllerGetCompanyConnectorStatusQuery({
      id,
    })

  const connectorInfo = useMemo(() => {
    if (!data) {
      return undefined
    }

    let status: ConnectionStatus = "healthy"

    // syncState "n/a" = the connector has no sync concept (write-path
    // connectors like google_ads_management, APP-470). For those, setupState
    // alone is authoritative and sync-recency guards must not apply — their
    // lastSuccessfulSyncDate is null forever by design.
    const hasSyncConcept = data.syncState !== "n/a"

    const setupState = data.setupState?.toLowerCase()
    if (
      setupState &&
      PENDING_SETUP_STATES.some(state => setupState.includes(state))
    ) {
      status = "pending_setup"
    } else if (
      !setupState ||
      UNHEALTHY_STATES.some(state => setupState === state)
    ) {
      // DB-driven unhealthy signal (broken / paused) takes precedence over
      // sync-recency so the UI reflects the source of truth immediately.
      status = "unhealthy"
    } else if (hasSyncConcept && !data.lastSuccessfulSyncDate) {
      // Guard the NaN case: a connection that has never synced should not be
      // reported as healthy just because there is no date to compare against.
      status = "unhealthy"
    } else if (hasSyncConcept && isSyncOutdated(data.lastSuccessfulSyncDate)) {
      status = "unhealthy"
    }

    return {
      status,
      isLastSyncOutdated:
        hasSyncConcept && isSyncOutdated(data.lastSuccessfulSyncDate),
      ...data,
    }
  }, [data])

  return { connectorInfo, isLoading }
}
